/**
 * Shared scrollbar styling for OC components
 * Provides consistent scrollbar appearance across the OC widget
 * Matches the behavior of online-community/Assets/projects/ocfe/styles/_scroll.scss
 */

import { css } from 'styled-components';

/**
 * OC scrollbar styling as a CSS mixin
 * Can be used with styled-components' css helper
 * 
 * @param scrollColor - Optional color for the scrollbar thumb (defaults to rgba(0, 0, 0, 0.2))
 * @param width - Optional width/height for the scrollbar (defaults to 8px)
 * 
 * Features:
 * - Scrollbar only appears on hover/focus
 * - No scrollbar arrows/buttons
 * - Transparent background
 * - Supports Firefox with scrollbar-color and scrollbar-width
 */
export const ocScrollbarStyles = (scrollColor: string = 'rgba(0, 0, 0, 0.2)', width: number = 8) => css`
  /* Webkit (Chrome, Safari, Edge) */
  &::-webkit-scrollbar {
    background: transparent;
    width: ${width}px;  /* width of vertical bar */
    height: ${width}px; /* height of horizontal bar */
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
  }

  /* Hide scrollbar arrows/buttons - both start and end */
  &::-webkit-scrollbar-button {
    display: none;
    height: 0;
    width: 0;
  }

  &::-webkit-scrollbar-button:start:decrement,
  &::-webkit-scrollbar-button:end:increment {
    display: none;
  }

  &:hover,
  &:focus {
    &::-webkit-scrollbar-thumb {
      border-radius: ${width}px;
      background: ${scrollColor};
    }

    &::-webkit-scrollbar {
      background-color: transparent;
    }
  }

  /* Firefox support - using @-moz-document hack like the original SCSS */
  @-moz-document url-prefix() {
    scrollbar-gutter: stable;
    scrollbar-color: transparent transparent;
    scrollbar-width: thin;

    &:hover,
    &:focus {
      scrollbar-color: ${scrollColor} transparent;
    }
  }
`;

